import { Box, IconButton, Modal } from "@mui/material";
import { FC, MouseEvent, SyntheticEvent, useRef, useState } from "react";
import { IconClose } from "@/assets/icons";
import { MOBILE_MAX_WIDTH } from "@/components/layout/MobileLayout/mobile-layout.styles";
import { usePinchZoomPan } from "@/hooks/usePinchZoomPan";
import { text } from "@/theme/colors";
import { safeArea } from "@/theme/variables";

/** Width that fills the frame unless the image would be taller than the viewport (`aspectRatio` = width / height) */
const fitWidth = (aspectRatio: number | null, viewportHeight: string) =>
  aspectRatio ? `min(100%, calc(${viewportHeight} * ${aspectRatio}))` : "100%";

/** Clicks arriving this soon after a pinch/drag are the browser's synthetic click — not a user tap */
const GESTURE_CLICK_GUARD_MS = 300;

type TImagePreviewModalProps = {
  open: boolean;
  imageUrl: string;
  onClose: () => void;
};

/**
 * Full-screen image preview.
 * Closes on the X icon or a tap on the empty area around the image (same as other detail modals).
 * Pinch to zoom, drag to pan while zoomed.
 */
export const ImagePreviewModal: FC<TImagePreviewModalProps> = ({ open, imageUrl, onClose }) => (
  <Modal open={open} onClose={onClose}>
    {/* Modal needs a ref-able child; content is unmounted while closed, so zoom/pan resets on every open */}
    <Box sx={{ outline: "none" }}>
      <ImagePreviewContent imageUrl={imageUrl} onClose={onClose} />
    </Box>
  </Modal>
);

const ImagePreviewContent: FC<Omit<TImagePreviewModalProps, "open">> = ({ imageUrl, onClose }) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const { transform, lastGestureEndRef } = usePinchZoomPan(imageRef);
  // Natural width/height ratio — lets the img box hug the visible image (no letterbox) while still filling the width
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  const handleImageLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    if (naturalWidth && naturalHeight) setAspectRatio(naturalWidth / naturalHeight);
  };

  const handleBackdropClick = (e: MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    if (Date.now() - lastGestureEndRef.current < GESTURE_CLICK_GUARD_MS) return;
    onClose();
  };

  return (
    // Frame matches the mobile layout width (centered) and clips the zoomed image so it never overflows the phone screen
    <Box
      onClick={handleBackdropClick}
      sx={{
        position: "fixed",
        inset: 0,
        mx: "auto",
        maxWidth: MOBILE_MAX_WIDTH,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: `calc(${safeArea.top} + 44px)`,
          right: 0,
          zIndex: 1,
          p: 0.5,
          color: text.white,
          opacity: 0.6,
        }}
      >
        <IconClose style={{ fontSize: 65 }} />
      </IconButton>

      <Box
        ref={imageRef}
        component="img"
        src={imageUrl}
        // Decorative vocabulary image — see FileImage for the alt rationale
        alt=""
        draggable={false}
        onLoad={handleImageLoad}
        style={{ transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})` }}
        sx={{
          // Fill the frame width, but for tall images shrink the width so the height fits the screen.
          // The box then equals the visible image, so taps beside it close the modal and pan bounds stay on the image.
          width: fitWidth(aspectRatio, "100vh"),
          "@supports (height: 100dvh)": { width: fitWidth(aspectRatio, "100dvh") },
          maxHeight: "100%",
          objectFit: "contain",
          touchAction: "none",
          userSelect: "none",
        }}
      />
    </Box>
  );
};
